import React, { useEffect, useId, useRef, useState } from 'react';
import {
  Box,
  Chip,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import LanguageDetectionService from '../../utils/languageDetection';

const languages = [
  {
    code: 'pt',
    label: 'PT-BR',
    name: 'Portuguese (Brazil)',
    flagSrc: '/images/seletor%20de%20idiomas/flag-pt-br.svg'
  },
  {
    code: 'en',
    label: 'EN',
    name: 'English',
    flagSrc: '/images/seletor%20de%20idiomas/flag-en.svg'
  },
  {
    code: 'es',
    label: 'ES',
    name: 'Spanish',
    flagSrc: '/images/seletor%20de%20idiomas/flag-es.svg'
  }
];

const resolveLangCode = (lng) => {
  if (!lng) return 'pt';

  const base = String(lng).split('-')[0].toLowerCase();
  return languages.some((language) => language.code === base) ? base : 'pt';
};

export default function LanguageSwitcher({ variant = 'icon', showLabel = false, autoDetect = false }) {
  const theme = useTheme();
  const { i18n, t } = useTranslation();
  const anchorRef = useRef(null);
  const menuId = `language-menu-${useId().replace(/:/g, '')}`;
  const [open, setOpen] = useState(false);
  const [isAutoDetected, setIsAutoDetected] = useState(false);
  const [detectionInfo, setDetectionInfo] = useState(null);

  const isSelectVariant = variant === 'select' || variant === 'button';
  // MUI lg starts at 1200px, complementing AppShell's max-width: 1199.95px compact CSS.
  const isResponsiveTopbar = variant === 'button';
  const shouldShowLabel = showLabel || isSelectVariant;

  useEffect(() => {
    const initializeLanguage = async () => {
      if (autoDetect && !localStorage.getItem('skipper_language')) {
        try {
          const detection = await LanguageDetectionService.initializeLanguage();

          if (detection.source === 'geolocation' && detection.language !== resolveLangCode(i18n.language)) {
            setIsAutoDetected(true);
            setDetectionInfo(detection.location);
            i18n.changeLanguage(detection.language);
          }
        } catch (error) {
          console.error('Language auto-detection failed:', error);
        }
      }
    };

    initializeLanguage();
  }, [autoDetect, i18n]);

  const handleToggle = () => {
    setOpen((currentOpen) => !currentOpen);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleLanguageChange = async (langCode) => {
    const oldLang = resolveLangCode(i18n.language);

    if (oldLang !== langCode) {
      await i18n.changeLanguage(langCode);
      LanguageDetectionService.trackLanguageChange(oldLang, langCode, 'manual_selection');
    }

    LanguageDetectionService.saveLanguagePreference(langCode);
    setIsAutoDetected(false);
    setDetectionInfo(null);
    handleClose();
  };

  const currentLanguage =
    languages.find((language) => language.code === resolveLangCode(i18n.language)) || languages[0];

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box
        className={`language-switcher${open ? ' is-open' : ''}`}
        data-language-switcher
        sx={{
          position: 'relative',
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 0.75
        }}
      >
        {!isSelectVariant && isAutoDetected && detectionInfo?.country && (
          <Chip
            icon={<PublicIcon sx={{ fontSize: 14 }} />}
            label={`Auto: ${detectionInfo.country}`}
            size="small"
            color="info"
            sx={{ height: 24, fontSize: '0.7rem' }}
          />
        )}

        <Box
          ref={anchorRef}
          component="button"
          type="button"
          className="language-toggle"
          data-language-toggle
          onClick={handleToggle}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
              event.preventDefault();
              setOpen(true);
            }

            if (event.key === 'Escape') {
              event.preventDefault();
              handleClose();
            }
          }}
          aria-label={t('common.changeLanguage', 'Change language')}
          aria-expanded={open ? 'true' : 'false'}
          aria-controls={menuId}
          aria-haspopup="menu"
          sx={{
            // Matches body.dashboard-shell .user-menu-trigger in
            // styles/appShell.css — invisible border + transparent bg by
            // default, pill radius, subtle tint on hover/focus. Keeps the
            // topbar language pill visually consistent with the user avatar
            // trigger right next to it.
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: isResponsiveTopbar ? { xs: 0.75, lg: 1.25 } : 1.25,
            minHeight: isSelectVariant ? 46 : 40,
            minWidth: shouldShowLabel
              ? (isResponsiveTopbar ? { xs: 72, lg: 124 } : 124)
              : 56,
            px: shouldShowLabel
              ? (isResponsiveTopbar ? { xs: 1, lg: 1.5 } : 1.5)
              : 1.1,
            py: 0.8,
            border: '1px solid transparent',
            borderRadius: '999px',
            background: 'transparent',
            color: theme.palette.text.primary,
            cursor: 'pointer',
            transition: 'background 0.15s ease, border-color 0.15s ease',
            outline: 'none',
            '&:hover': {
              background: theme.palette.action.hover,
              borderColor: theme.palette.divider
            },
            '&:focus-visible': {
              background: theme.palette.action.hover,
              borderColor: theme.palette.divider
            }
          }}
        >
          <Box
            className="language-current"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}
          >
            <Box
              component="img"
              src={currentLanguage.flagSrc}
              alt=""
              width={20}
              height={14}
              data-language-flag
              sx={{
                display: 'block',
                borderRadius: '3px',
                boxShadow: '0 1px 2px rgba(15, 23, 42, 0.18)'
              }}
            />
            {shouldShowLabel && (
              <Typography
                component="span"
                data-language-label
                sx={{
                  display: isResponsiveTopbar ? { xs: 'none', lg: 'inline' } : 'inline',
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  lineHeight: 1,
                  letterSpacing: '0.04em'
                }}
              >
                {currentLanguage.label}
              </Typography>
            )}
          </Box>
          <KeyboardArrowDownIcon
            aria-hidden="true"
            sx={{
              fontSize: 20,
              color: theme.palette.text.secondary,
              transition: 'transform 0.2s ease',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
            }}
          />
        </Box>

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-end"
          transition
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps} style={{ transformOrigin: 'top right' }}>
              <Paper
                id={menuId}
                className="language-menu"
                role="menu"
                aria-label={t('common.languageMenu', 'Language selection menu')}
                data-language-menu
                elevation={0}
                sx={{
                  mt: 1,
                  minWidth: anchorRef.current ? anchorRef.current.offsetWidth : 156,
                  borderRadius: '16px',
                  border: '1px solid rgba(15, 23, 42, 0.12)',
                  background: 'rgba(255, 255, 255, 0.98)',
                  boxShadow: '0 22px 48px rgba(15, 23, 42, 0.16)',
                  overflow: 'hidden'
                }}
              >
                {isAutoDetected && detectionInfo?.country && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1.5,
                      py: 1,
                      borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
                      color: '#64748b'
                    }}
                  >
                    <PublicIcon sx={{ fontSize: 14 }} />
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                      Auto-detected from {detectionInfo.country}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ p: 0.75 }}>
                  {languages.map((language) => {
                    const isActive = language.code === currentLanguage.code;

                    return (
                      <Box
                        key={language.code}
                        component="button"
                        type="button"
                        className={`language-option${isActive ? ' is-active' : ''}`}
                        data-language-option={language.code}
                        aria-label={language.name}
                        role="menuitemradio"
                        aria-checked={isActive}
                        onClick={() => handleLanguageChange(language.code)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleLanguageChange(language.code);
                          }
                        }}
                        sx={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 1.1,
                          py: 1,
                          border: 'none',
                          borderRadius: '12px',
                          background: isActive ? 'rgba(1, 111, 244, 0.09)' : 'transparent',
                          color: isActive ? '#016FF4' : '#0f172a',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            background: isActive ? 'rgba(1, 111, 244, 0.12)' : 'rgba(15, 23, 42, 0.05)'
                          },
                          '&:focus-visible': {
                            outline: 'none',
                            boxShadow: 'inset 0 0 0 2px rgba(1, 111, 244, 0.18)'
                          }
                        }}
                      >
                        <Box
                          component="img"
                          src={language.flagSrc}
                          alt=""
                          width={20}
                          height={14}
                          sx={{
                            display: 'block',
                            borderRadius: '3px',
                            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.18)'
                          }}
                        />
                        <Typography
                          component="span"
                          sx={{
                            flexGrow: 1,
                            textAlign: 'left',
                            fontSize: '0.85rem',
                            fontWeight: isActive ? 700 : 600,
                            letterSpacing: '0.04em'
                          }}
                        >
                          {language.label}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Grow>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
